import { Fragment, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { GripVertical, HelpCircle, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { IconSort, IconSortAsc, IconSortDesc } from '../../v2/icons';
import { TableScrollRail } from '@/v2/ui/TableScrollRail';
import { maxScrollOf, type RailMetrics } from '@/v2/ui/scrollRailGeometry';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { columnFilterMatches } from '@/components/ui/dataTableFilter';
import {
  deriveOrderedColumns,
  humanizeKey,
  reorderKeys,
} from '@/components/ui/dataTableColumns';
import { pageTick, togglePage, toggleRow } from '@/components/ui/dataTableSelection';
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs';
import { useTableMetrics } from '@/hooks/useTableMetrics';

/**
 * The DataTable (102) — SONAR's crown jewel and ServWell's hardened frame, merged.
 *
 * ⚠️ ServWell-AUTHORED — NOT an upstream copy. It lives beside the vendored primitives
 * because it is their consumer-of-record, but a re-vendor sweep must never overwrite it;
 * the only verbatim-vendored table machinery is dataTableColumns.ts / dataTableFilter.ts.
 *
 * The FRAME is ServWell's, kept whole because every piece of it was measured into
 * existence: the CARD that must never clip (012), the single silent scroller with the
 * viewport-pinned rail, the conditional sticky identity column, server mode whose footer
 * count is a COUNT, the em-dash law, tri-state sort with real aria. Sonar's own wrapper
 * is `rounded-lg border overflow-hidden` — the exact class 012 spent a ticket killing —
 * which is why the port went features-into-frame and not frame-into-features.
 *
 * The FEATURES are Sonar's, ported with their lessons intact: per-column filter inputs
 * (token-prefix matching via `columnFilterMatches`), drag-reorder + show/hide column
 * customization persisted per-user (`useTableColumnPrefs` + `deriveOrderedColumns`),
 * header tooltips for columns whose name alone could mislead, default-hidden columns,
 * and client-mode footer cells. Deliberately NOT ported yet — each waits for its first
 * real consumer rather than shipping speculative surface: grouped header bands, and
 * `onVisibleRowsChange`.
 *
 * Row SELECTION arrived with its first consumer, the Jobs list's "Set service type" (ops
 * insights 08), and NOT as Sonar's `SELECT_COLUMN_KEY` fold-in: a checkbox COLUMN could be
 * hidden, or dragged into slot 0, where the row's activator button would wrap it (nested
 * interactive elements, the 103 finding under `isRowClickable`). The boxes ride in slot 0
 * instead, BESIDE the activator, whatever column is first: never hidden, pinned with the
 * identity when the table pans, and never inside another control. See `selection`.
 *
 * ── Two modes, and which one a list gets ──
 *
 * WITHOUT `server`: the table filters, sorts and pages the array it was handed. Correct
 * for lists bounded by a parent — the contacts on one customer, the visits on one job.
 *
 * WITH `server`: the table renders exactly the rows it was given and reports every
 * interaction upward — search, sort, page and per-column filters all happen in SQL.
 * Required for lists bounded only by how big the business gets. The footer's second
 * number comes from a COUNT, so "50 of 812" is a fact rather than the length of
 * whatever array is in memory. Per-column filter inputs render in server mode ONLY when
 * the page supplies `server.onFilters` — inputs that look editable while silently doing
 * nothing are Sonar's own stated anti-rule.
 */

/** Data-honesty law: missing/unknown renders an em-dash — never 0, never blank. */
export function dash(v: string | number | null | undefined): ReactNode {
  return v === null || v === undefined || v === '' ? '—' : v;
}

export type DataTableColumn<T> = {
  key: string;
  label: string;
  cell: (row: T) => ReactNode;
  /** Sort key. Omit → column unsortable. */
  value?: (row: T) => string | number | null | undefined;
  /**
   * Text the global search matches. Defaults to `value`. Set this whenever `value` is
   * a machine number the user never sees (epoch millis, cents) so searching "2026"
   * can't hit an invisible timestamp; return null to exclude the column from search.
   */
  searchValue?: (row: T) => string | number | null | undefined;
  /**
   * Renders a filter input under this column's header. Client mode matches with
   * `columnFilterMatches` against `searchValue ?? value` (the user filters what they
   * can see). Server mode forwards the value upward under this column's key — the
   * page's service resolves it through its own allow-list, so enable this only on
   * columns the server actually filters.
   */
  filterable?: boolean;
  /** Hover tooltip on the header — for metrics whose name alone could mislead. */
  labelTooltip?: string;
  /**
   * Ships HIDDEN by default: present in the columns picker, off until the user turns it
   * on. Requires `tableId`. A user with saved prefs is unaffected — their set wins.
   */
  defaultHidden?: boolean;
  /** Client-mode footer cell — receives the filtered (pre-paginate) rows. */
  footer?: (filteredRows: T[]) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
};

export type Sort = { key: string; dir: 'asc' | 'desc' } | null;

/**
 * Row selection: the page owns the set of ticked row keys (like the Schedule page's), and the
 * table draws a box on each row and one in the header for the page on screen, and hands back the
 * next set (`dataTableSelection.ts` holds the rules).
 */
export type RowSelection<T> = {
  /** The keys ticked, of the rows on screen. */
  ticked: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
  /**
   * A row's box, by name: the SAME name the row's activator carries, after "Select" (the Jobs
   * list: "Select job #12 No heat" beside "Preview job #12 No heat").
   */
  label: (row: T) => string;
  /** The header box, by name: "Select every job on this page". */
  pageLabel: string;
};

/**
 * One box. A native checkbox: Space ticks it, it takes focus and shows it. `mixed` is the DOM's
 * `indeterminate`, a property with no attribute, set as the element mounts.
 *
 * The box is 14px, so it sits in a 24px `<label>` (WCAG 2.2's minimum target): a press anywhere
 * in that square ticks it. The label stops the press there, so neither a press on the box nor
 * one beside it reaches the row, whose click opens the preview. (A press on the label reaches
 * the box as a second click, which bubbles through the same label and stops there too.)
 */
function SelectBox({
  checked,
  mixed = false,
  label,
  onToggle,
}: {
  checked: boolean;
  mixed?: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <label
      className="inline-flex size-6 shrink-0 cursor-pointer items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="size-3.5 accent-[var(--sw-brand)]"
        checked={checked}
        ref={(el) => {
          if (el) el.indeterminate = mixed;
        }}
        aria-label={label}
        onChange={onToggle}
      />
    </label>
  );
}

/** A row's box, wired to the page's set. No hooks, so a test can call it and press it. */
export function RowSelectBox<T>({
  selection,
  row,
  rowKey,
}: {
  selection: RowSelection<T>;
  row: T;
  rowKey: string;
}) {
  return (
    <SelectBox
      checked={selection.ticked.has(rowKey)}
      label={selection.label(row)}
      onToggle={() => selection.onChange(toggleRow(selection.ticked, rowKey))}
    />
  );
}

/** The header's box, for the rows on this page. No hooks, like the row's. */
export function PageSelectBox<T>({
  selection,
  pageKeys,
}: {
  selection: RowSelection<T>;
  pageKeys: readonly string[];
}) {
  const tick = pageTick(selection.ticked, pageKeys);
  return (
    <SelectBox
      checked={tick === 'all'}
      mixed={tick === 'some'}
      label={selection.pageLabel}
      onToggle={() => selection.onChange(togglePage(selection.ticked, pageKeys))}
    />
  );
}

const PAGE_SIZE = 20;

/**
 * The card around the table — and the ONE class it must never carry again.
 *
 * ⚠️ `overflow-hidden` lived here for three months to round the table's corners, and
 * `overflow: hidden` makes an element a SCROLLPORT. So `TableScrollRail`'s
 * `sticky bottom` resolved against this card rather than the viewport, and the rail
 * pinned itself to the bottom of a 6,000px table: measured **286px below the fold** on
 * `/customers`. It answered drag, click, keyboard and wheel perfectly the whole time.
 * It failed the only test that matters, which is being on screen.
 *
 * Exported as a string so `scroll-rail.test.ts` asserts the VALUE, not a shape.
 */
export const CARD = 'rounded-[var(--radius)] border border-border bg-surface';

/** The `activeKey` row's tint, layered over a cell's background colour. */
const ACTIVE_ROW_TINT = {
  backgroundImage: 'linear-gradient(var(--sw-brand-pill), var(--sw-brand-pill))',
} as const;

/**
 * Column 0 stays put while the rest of the table pans sideways. Not decoration: column 0
 * holds the row's ONLY keyboard and screen-reader activator, so the moment the rail
 * works, panning right would carry the only way to open a row off the screen.
 */
const IDENTITY_CELL = 'sticky left-0 z-[1] bg-surface';

/**
 * The same pin in the footer, painted in the footer's own `bg-surface-2`: its first cell holds
 * the row's label ("Total"), which must stay beside the figures it labels while the rest pans
 * (Phase 2 QA, R2-m3: it scrolled away and left a cut figure at the left).
 */
const IDENTITY_FOOTER_CELL = 'sticky left-0 z-[1] bg-surface-2';

/** The seam, drawn only once something is actually sliding underneath. */
const IDENTITY_EDGE = 'shadow-[1px_0_0_var(--sw-border)]';

/**
 * The most of the scrollport a pinned column may occupy.
 *
 * 🔴 Found by opening the page, not by reading it. `/customers` at 390px has a **392px**
 * identity column in a **356px** port — the name plus a company sub-line, `nowrap`. Pin
 * that and it covers the table completely: panning right reveals nothing but the same
 * names, and the columns the pan was FOR stay hidden underneath. A sticky column that
 * occludes the table is strictly worse than no sticky column.
 *
 * So the pin is conditional on measurement rather than on a breakpoint. A breakpoint
 * would be a guess about column widths, and column widths come from the shop's own data —
 * a shop whose customers are all "Smith Residence" and one whose customers are parish
 * halls do not have the same table at the same width.
 *
 * The underlying problem is the 392px column itself, and narrowing it means truncating
 * the one field that identifies the row. That is a design ruling, not a bug fix —
 * ticket 037 Group A holds it, unchanged by this port.
 */
export const MAX_IDENTITY_SHARE = 0.5;

/**
 * A string that changes whenever the column SET does — the dependency of the effect
 * that attaches the identity observer. Must be INJECTIVE, which a join is not:
 * `['a|b','c']` and `['a','b|c']` join alike, and a node-for-node replaced column set
 * would leave the observer on a detached `<th>`, pinning a stale width.
 */
export function columnSetKey(columns: { key: string }[]): string {
  return JSON.stringify(columns.map((c) => c.key));
}

export type TableMetrics = RailMetrics & {
  /** Rendered width of column 0, or 0 before the first measurement. */
  identityWidth: number;
};

/** Whether pinning column 0 would still leave room to read the rest of the table. */
export function identityFitsPort(m: TableMetrics): boolean {
  if (m.identityWidth <= 0 || m.clientWidth <= 0) return false;
  return m.identityWidth <= m.clientWidth * MAX_IDENTITY_SHARE;
}

/**
 * The state a server-driven table hands back to its page. The page owns it (it has to —
 * it's what goes into the query), and the table just reports the clicks.
 */
export type ServerTable = {
  /** Rows matching the search+filters across the WHOLE table — a COUNT, never a length. */
  total: number;
  page: number;
  onPage: (page: number) => void;
  pageSize: number;
  sort: Sort;
  onSort: (sort: Sort) => void;
  /**
   * Per-column filter values, keyed by column key. Present (with `onFilters`) → the
   * table renders filter inputs for `filterable` columns and forwards edits upward;
   * absent → no inputs render, because an input that does nothing is a lie.
   */
  filters?: Record<string, string>;
  onFilters?: (filters: Record<string, string>) => void;
  /** True while a new page or search is in flight, so the footer can say so. */
  loading?: boolean;
};

function normalize(s: string): string {
  return s.toLowerCase().replace(/[$,%\s]/g, '');
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  search = '',
  onRowClick,
  isRowClickable,
  empty,
  rowLabel,
  rowOpens,
  server,
  tableId,
  footerLabel,
  selection,
  activeKey,
  revealRowKey,
  rowGroup,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Presentation-only headers for consecutive runs; never regroup or sort rows. */
  rowGroup?: { key: (row: T) => string; render: (row: T) => ReactNode };
  /** Feed the page's SearchInput value here; the table filters + shows "N of M". */
  search?: string;
  onRowClick?: (row: T) => void;
  /**
   * Per-row gate for `onRowClick`: a row is interactive only if this returns true —
   * non-clickable rows get no cursor, no activator button, no handlers (rows that
   * would dead-end a drawer). Absent → every row is clickable when onRowClick is set.
   * Ticket-mandated (102) ahead of a consumer — the one exception to this file's
   * defer-to-first-consumer rule. 103 considered report rows and DECLINED: report
   * column 0 is often a LinkCell, and the column-0 activator button wrapping a <Link>
   * nests interactive elements — reachable by any user who drags a link column into
   * slot 0. A consumer needs cells column 0 can safely wrap.
   */
  isRowClickable?: (row: T) => boolean;
  /** True-empty state (no data at all) — an <EmptyState>. Never used for errors. */
  empty: ReactNode;
  /** Accessible name for the row's activator, e.g. (c) => `Preview ${c.name}`. */
  rowLabel?: (row: T) => string;
  /**
   * What activating a row opens, when it opens one: `'dialog'` for a side panel or a sheet,
   * said on the activator as `aria-haspopup`, so a screen reader hears that the row opens
   * something over the page rather than going somewhere.
   */
  rowOpens?: 'dialog';
  /** Present → search, sort, paging and filters are the server's. */
  server?: ServerTable;
  /**
   * Label for the footer ROW (e.g. "Total (19 rows)") — rendered in the first VISIBLE
   * column's footer cell when that column has no footer of its own. A label attached to
   * an authored column vanishes when the user hides that column and strands mid-row
   * when they reorder it (Codex 103 C3); the row's label belongs to the row.
   */
  footerLabel?: (filteredRows: T[]) => ReactNode;
  /**
   * Stable, globally-unique id for this logical table ("customers"). Present → the
   * user can drag-reorder and show/hide columns, persisted per-user via
   * trpc.userPrefs. Absent → 100% legacy behavior. Distinct id per logical table,
   * even for sibling tables in one file.
   */
  tableId?: string;
  /**
   * Present → a box on every row and one in the header for the page, in slot 0 beside the
   * row's activator (see the module note). The page owns the set; show only rows it may act on.
   */
  selection?: RowSelection<T>;
  /**
   * The row the page has open (its panel, a deep link's target): marked `aria-current` and
   * tinted, and findable by `data-row-key` so the page can scroll it into view (UX baseline:
   * a link lands on the exact item).
   */
  activeKey?: string;
  /** Client mode only: reveal this stable row on its existing sorted/filtered page.
   * The caller clears the request after its own target control mounts; missing/filtered
   * targets do not change filters or invent rows. Server paging stays caller-owned. */
  revealRowKey?: string;
}) {
  const [localSort, setLocalSort] = useState<Sort>(null);
  const [localPage, setLocalPage] = useState(0);
  const [localFilters, setLocalFilters] = useState<Record<string, string>>({});
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const scrollerId = useId();

  // ── Column customization (drag-reorder + show/hide) — Sonar's, whole ─────────
  const prefsEnabled = !!tableId;
  const defaultHiddenKeys = useMemo(
    () => columns.filter((c) => c.defaultHidden).map((c) => c.key),
    [columns],
  );
  const prefs = useTableColumnPrefs(
    prefsEnabled ? tableId : undefined,
    defaultHiddenKeys,
  );

  // Full ordered list (user's order applied, hidden NOT removed) — the reorder space,
  // so a hidden column keeps its saved slot and re-showing restores its place.
  const fullOrderedColumns = useMemo(
    () => (prefsEnabled ? deriveOrderedColumns(columns, prefs.order, []) : columns),
    [prefsEnabled, columns, prefs.order],
  );
  // Rule 3 (visibility last) belongs to the helper — deriving it here with a local Set
  // would leave the helper's documented branch production-dead.
  const orderedColumns = useMemo(
    () =>
      prefsEnabled ? deriveOrderedColumns(columns, prefs.order, prefs.hidden) : columns,
    [prefsEnabled, columns, prefs.order, prefs.hidden],
  );

  const visibleCount = orderedColumns.length;

  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const commitReorder = (targetKey: string) => {
    setDragOverKey(null);
    const source = dragKey;
    setDragKey(null);
    if (!source || source === targetKey) return;
    prefs.setOrder(
      reorderKeys(
        fullOrderedColumns.map((c) => c.key),
        source,
        targetKey,
      ),
    );
  };

  // A string, not the array: `columns` is usually rebuilt on every render, and this is
  // a dependency of an effect that attaches observers. Keyed on the RENDERED set so a
  // reorder that changes column 0 re-attaches the identity observer.
  const columnKeys = useMemo(() => columnSetKey(orderedColumns), [orderedColumns]);
  const measured = useTableMetrics(scroller, columnKeys);
  const metrics: TableMetrics = { ...measured, identityWidth: measured.columnWidth };
  const overflows = maxScrollOf(metrics) > 0;
  /** Pin column 0 only where there is something to pin it against, and room to do it. */
  const pinIdentity = overflows && identityFitsPort(metrics);
  const pannedRight = pinIdentity && metrics.scrollLeft > 0;

  const sort = server ? server.sort : localSort;
  const setSort = server ? server.onSort : setLocalSort;
  const setPage = server ? server.onPage : setLocalPage;
  const filters = server ? (server.filters ?? {}) : localFilters;

  // Server mode renders filter inputs only when the page wired `onFilters` — otherwise
  // the inputs would look editable while silently doing nothing (Sonar's own rule).
  const filtersAvailable = server ? !!server.onFilters : true;
  const filterableColumns = filtersAvailable
    ? orderedColumns.filter((c) => c.filterable)
    : [];

  const setFilter = (key: string, value: string) => {
    const next = { ...filters };
    if (value.trim()) next[key] = value;
    else delete next[key];
    if (server) server.onFilters?.(next);
    else {
      setLocalFilters(next);
      setLocalPage(0);
    }
  };

  const activeFilterCount = Object.values(filters).filter((v) => v.trim()).length;

  // In server mode `rows` is already the answer: filtered, sorted and sliced in SQL.
  // Re-filtering it here would hide rows the server deliberately matched.
  const filtered = useMemo(() => {
    if (server) return rows;
    let result = rows;
    const q = normalize(search);
    if (q) {
      result = result.filter((r) =>
        orderedColumns.some((c) => {
          const accessor = c.searchValue ?? c.value;
          const v = accessor?.(r);
          return v !== null && v !== undefined && normalize(String(v)).includes(q);
        }),
      );
    }
    // Per-column filters: token-prefix match, AND across columns. A hidden column's
    // filter is inert by construction — only rendered (ordered) columns are consulted.
    for (const col of orderedColumns) {
      const fv = filters[col.key];
      if (!fv?.trim() || !col.filterable) continue;
      const accessor = col.searchValue ?? col.value;
      result = result.filter((r) => columnFilterMatches(accessor?.(r), fv));
    }
    return result;
  }, [rows, orderedColumns, search, filters, server]);

  const sorted = useMemo(() => {
    if (server || !sort) return filtered;
    const col = orderedColumns.find((c) => c.key === sort.key);
    if (!col?.value) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = col.value!(a);
      const bv = col.value!(b);
      // Blanks always sink, in a way that stays a consistent comparator.
      const aBlank = av === null || av === undefined || av === '';
      const bBlank = bv === null || bv === undefined || bv === '';
      if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [filtered, sort, orderedColumns, server]);

  const pageSize = server?.pageSize ?? PAGE_SIZE;
  const matched = server ? server.total : sorted.length;
  const pageCount = Math.max(1, Math.ceil(matched / pageSize));
  const safePage = Math.min(server ? server.page : localPage, pageCount - 1);
  // Drawing the clamp is not enough in server mode: the DISPLAY said page 1 while the
  // REQUEST still carried the stale offset, so a filter that shrank the result set past
  // the current page showed "0 of N" forever. Report the clamp upward once settled.
  const serverPageStale =
    !!server && !server.loading && server.page > pageCount - 1 && matched >= 0;
  useEffect(() => {
    if (serverPageStale) server!.onPage(pageCount - 1);
  }, [serverPageStale, pageCount, server]);
  const pageRows = server
    ? sorted
    : sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const pageKeys = pageRows.map(rowKey);

  useEffect(() => {
    if (server || revealRowKey === undefined) return;
    const index = sorted.findIndex((row) => rowKey(row) === revealRowKey);
    if (index >= 0) setLocalPage(Math.floor(index / PAGE_SIZE));
  }, [server, revealRowKey, sorted, rowKey]);

  const hasFooter = orderedColumns.some((c) => c.footer) && !server;
  // The footer row's LABEL cell: the first VISIBLE column without a footer of its own.
  // Index 0 alone dropped the label whenever a totaled column was dragged into slot 0
  // (Codex R2-2); -1 (every visible column carries a total) drops the label, which is
  // the only cell-less outcome left.
  const footerLabelIndex = hasFooter ? orderedColumns.findIndex((c) => !c.footer) : -1;

  function cycleSort(key: string) {
    // Tri-state: asc → desc → the list's own order.
    const next: Sort =
      sort?.key !== key
        ? { key, dir: 'asc' }
        : sort.dir === 'asc'
          ? { key, dir: 'desc' }
          : null;
    setSort(next);
    setPage(0);
  }

  const filtering = normalize(search).length > 0 || activeFilterCount > 0;
  // In server mode an empty page while filtering is "no matches", not "no data". In
  // CLIENT mode an empty `rows` is true-empty regardless of any filter text — the parent
  // handed over nothing, and no filter changes that. scroll-rail.test.ts pins this
  // predicate by value.
  if (rows.length === 0 && !(server && filtering)) return <>{empty}</>;

  /** A body cell's content: column 0 of an interactive row is the row's activator button. */
  const cellContent = (
    c: DataTableColumn<T>,
    r: T,
    i: number,
    interactive: boolean,
  ): ReactNode =>
    i === 0 && interactive ? (
      <button
        type="button"
        aria-label={rowLabel?.(r)}
        aria-haspopup={rowOpens}
        onClick={(e) => {
          e.stopPropagation();
          onRowClick!(r);
        }}
        className="w-full text-left"
      >
        {c.cell(r)}
      </button>
    ) : (
      c.cell(r)
    );

  const headerDropProps = (colKey: string) =>
    prefsEnabled
      ? {
          onDragOver: (e: React.DragEvent) => {
            if (!dragKey) return; // ignore drags that didn't start on a column grip
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (dragOverKey !== colKey) setDragOverKey(colKey);
          },
          onDrop: (e: React.DragEvent) => {
            if (!dragKey) return;
            e.preventDefault();
            commitReorder(colKey);
          },
        }
      : {};

  return (
    <div className={CARD}>
      {prefsEnabled ? (
        <div className="flex justify-end border-b border-border px-2 py-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Columns
                {prefs.hidden.length > 0 ? (
                  <span className="ml-0.5 rounded bg-muted px-1 text-[10px] leading-4">
                    {prefs.hidden.length}
                  </span>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-[60vh] w-56 overflow-y-auto"
            >
              <DropdownMenuLabel className="text-xs">Show columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {fullOrderedColumns.map((col) => {
                const visible = !prefs.hidden.includes(col.key);
                // Never let the user hide the last visible column.
                const isLastVisible = visible && visibleCount === 1;
                return (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={visible}
                    disabled={isLastVisible}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(next) => {
                      prefs.toggleHidden(col.key);
                      // Hiding drops the column's active filter so re-showing it can't
                      // silently reapply a surprise constraint.
                      if (!next && filters[col.key]) setFilter(col.key, '');
                      // …and its active SORT, for the same reason a step further: a
                      // hidden sort column keeps ordering the table with no header
                      // showing it and no control able to cancel it (Codex 103 C1).
                      if (!next && sort?.key === col.key) {
                        setSort(null);
                        setPage(0);
                      }
                    }}
                    className="text-xs"
                  >
                    {col.label || humanizeKey(col.key)}
                  </DropdownMenuCheckboxItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  prefs.reset();
                  // Reset re-HIDES the default-hidden set — release any sort or filter
                  // riding on a column it hides, the same rule as the checkbox above
                  // (Codex R2-1). One batched write for the filters: clearing them one
                  // setFilter at a time would spread the same stale object and keep
                  // only the last deletion.
                  if (sort && defaultHiddenKeys.includes(sort.key)) {
                    setSort(null);
                    setPage(0);
                  }
                  const stale = defaultHiddenKeys.filter((k) => filters[k]?.trim());
                  if (stale.length > 0) {
                    const next = { ...filters };
                    for (const k of stale) delete next[k];
                    if (server) server.onFilters?.(next);
                    else {
                      setLocalFilters(next);
                      setLocalPage(0);
                    }
                  }
                }}
                // Already at the SHIPPED default = no custom order AND hidden == the
                // default-hidden set (NOT hidden.length===0 — an empty set means the
                // user REVEALED the default-hidden columns, which Reset should undo).
                disabled={
                  prefs.order.length === 0 &&
                  prefs.hidden.length === defaultHiddenKeys.length &&
                  prefs.hidden.every((k) => defaultHiddenKeys.includes(k))
                }
                className="gap-1.5 text-xs"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset columns
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      <div
        ref={setScroller}
        id={scrollerId}
        className="h-scroll-silent"
        // Keyboard focus left of the pan lands flush at the port's left edge — directly
        // under the pinned column, which paints over it.
        style={pinIdentity ? { scrollPaddingLeft: metrics.identityWidth } : undefined}
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              {orderedColumns.map((c, i) => {
                const active = sort?.key === c.key;
                const SortIcon = !active
                  ? IconSort
                  : sort!.dir === 'asc'
                    ? IconSortAsc
                    : IconSortDesc;
                const isDropTarget =
                  prefsEnabled && dragOverKey === c.key && !!dragKey && dragKey !== c.key;
                return (
                  <th
                    key={c.key}
                    aria-sort={
                      active
                        ? sort!.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    {...headerDropProps(c.key)}
                    className={`h-10 px-2 text-xs font-medium text-muted-foreground first:pl-4 last:pr-4 ${
                      c.align === 'right' ? 'text-right' : 'text-left'
                    } ${i === 0 && pinIdentity ? IDENTITY_CELL : ''} ${
                      i === 0 && pannedRight ? IDENTITY_EDGE : ''
                    } ${isDropTarget ? 'border-l-2 border-l-brand' : ''}`}
                  >
                    {i === 0 && selection ? (
                      <span className="mr-1 inline-flex align-middle">
                        <PageSelectBox selection={selection} pageKeys={pageKeys} />
                      </span>
                    ) : null}
                    <span
                      className={`inline-flex items-center gap-1 ${
                        c.align === 'right' ? 'flex-row-reverse' : ''
                      }`}
                    >
                      {prefsEnabled ? (
                        // The reorder grip — the ONLY drag source, so a click on the
                        // header still sorts and never gets hijacked into a drag. A real
                        // <button>, because the workspace law says every interactive
                        // element is keyboard-operable: ← and → move the column one slot,
                        // which is the whole of what the drag can do.
                        <button
                          type="button"
                          draggable
                          onDragStart={(e) => {
                            setDragKey(c.key);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', c.key);
                          }}
                          onDragEnd={() => {
                            setDragKey(null);
                            setDragOverKey(null);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                            e.preventDefault();
                            e.stopPropagation();
                            const order = fullOrderedColumns.map((col) => col.key);
                            const at = order.indexOf(c.key);
                            const to = order[e.key === 'ArrowLeft' ? at - 1 : at + 1];
                            if (to !== undefined)
                              prefs.setOrder(reorderKeys(order, c.key, to));
                          }}
                          aria-label={`Reorder the ${c.label || humanizeKey(c.key)} column (arrow keys move it; drag also works)`}
                          title="Reorder column — drag, or focus and press ←/→"
                          className="inline-flex shrink-0 cursor-grab text-muted-foreground/40 hover:text-text active:cursor-grabbing"
                        >
                          <GripVertical className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                      {c.value ? (
                        <button
                          type="button"
                          onClick={() => cycleSort(c.key)}
                          className={`inline-flex items-center gap-1 hover:text-text ${
                            c.align === 'right' ? 'flex-row-reverse' : ''
                          }`}
                        >
                          {c.label}
                          <SortIcon size={13} className={active ? '' : 'opacity-30'} />
                        </button>
                      ) : (
                        c.label
                      )}
                      {c.labelTooltip ? (
                        <span title={c.labelTooltip} className="inline-flex cursor-help">
                          <HelpCircle className="h-3 w-3 text-muted-foreground/60" />
                        </span>
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
            {filterableColumns.length > 0 ? (
              <tr className="border-b border-border">
                {orderedColumns.map((c, i) => (
                  <th
                    key={`filter-${c.key}`}
                    className={`px-2 py-1 first:pl-4 last:pr-4 ${
                      i === 0 && pinIdentity ? IDENTITY_CELL : ''
                    } ${i === 0 && pannedRight ? IDENTITY_EDGE : ''}`}
                  >
                    {c.filterable ? (
                      // Hand-rolled rather than the vendored <Input> ON PURPOSE: this row
                      // is 28px dense chrome speaking the frame's --sw-* vocabulary; the
                      // shadcn Input is h-9 with its own ring model, wrong on both counts.
                      <input
                        type="text"
                        value={filters[c.key] ?? ''}
                        onChange={(e) => setFilter(c.key, e.target.value)}
                        placeholder="Filter…"
                        aria-label={`Filter by ${c.label || humanizeKey(c.key)}`}
                        className="h-7 w-full min-w-16 rounded-[var(--radius-sm)] border border-border bg-surface-2 px-2 text-[11px] font-normal text-text placeholder:text-muted-foreground"
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            ) : null}
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={orderedColumns.length}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  {/* Name what the user actually did — a filter is not a search. */}
                  {server
                    ? `Nothing matches ${normalize(search) ? 'that search' : 'those filters'}.`
                    : `No results matching your ${normalize(search) ? 'search' : 'filters'} (${rows.length} total)`}
                </td>
              </tr>
            ) : (
              pageRows.map((r, rowIndex) => {
                // A row is interactive when onRowClick is set AND the gate (if any)
                // passes. Non-interactive rows get no cursor, no activator, no handler.
                const interactive =
                  !!onRowClick && (!isRowClickable || isRowClickable(r));
                const key = rowKey(r);
                const active = activeKey !== undefined && key === activeKey;
                return (
                  <Fragment key={key}>
                    {rowGroup &&
                    (rowIndex === 0 ||
                      rowGroup.key(pageRows[rowIndex - 1]!) !== rowGroup.key(r)) ? (
                      <tr
                        data-row-group={rowGroup.key(r)}
                        className="border-y border-border bg-surface-2"
                      >
                        <td colSpan={orderedColumns.length} className="p-2 first:pl-4">
                          {rowGroup.render(r)}
                        </td>
                      </tr>
                    ) : null}
                    <tr
                      data-row-key={key}
                      aria-current={active ? 'true' : undefined}
                      // Mouse convenience only. The keyboard/screen-reader affordance is
                      // the real <button> in the first cell.
                      onClick={interactive ? () => onRowClick!(r) : undefined}
                      className={`group border-b border-border last:border-b-0 ${
                        interactive ? 'cursor-pointer hover:bg-surface-2' : ''
                      }`}
                    >
                      {orderedColumns.map((c, i) => (
                        <td
                          key={c.key}
                          // The open row's tint is painted OVER each cell's own surface (an image
                          // above the colour), so the sticky identity cell, which paints its own
                          // opaque background, is tinted too and still hides what scrolls under it.
                          style={active ? ACTIVE_ROW_TINT : undefined}
                          className={`whitespace-nowrap p-2 first:pl-4 last:pr-4 ${
                            c.align === 'right' ? 'text-right tabular-nums' : ''
                          } ${
                            i === 0 && pinIdentity
                              ? // The sticky cell paints its own background, so the row's
                                // hover state is handed to it explicitly.
                                `${IDENTITY_CELL} ${interactive ? 'group-hover:bg-surface-2' : ''}`
                              : ''
                          } ${i === 0 && pannedRight ? IDENTITY_EDGE : ''} ${c.className ?? ''}`}
                        >
                          {i === 0 && selection ? (
                            // The box BESIDE the activator, never inside it: two controls,
                            // each reachable on its own.
                            <div className="flex items-center gap-1">
                              <RowSelectBox
                                selection={selection}
                                row={r}
                                rowKey={rowKey(r)}
                              />
                              <div className="min-w-0 flex-1">
                                {cellContent(c, r, i, interactive)}
                              </div>
                            </div>
                          ) : (
                            cellContent(c, r, i, interactive)
                          )}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                );
              })
            )}
          </tbody>
          {hasFooter && sorted.length > 0 ? (
            <tfoot className="border-t border-border bg-surface-2 text-sm font-medium">
              <tr>
                {orderedColumns.map((c, i) => (
                  <td
                    key={`footer-${c.key}`}
                    className={`whitespace-nowrap p-2 first:pl-4 last:pr-4 ${
                      c.align === 'right' ? 'text-right tabular-nums' : ''
                    } ${i === 0 && pinIdentity ? IDENTITY_FOOTER_CELL : ''} ${
                      i === 0 && pannedRight ? IDENTITY_EDGE : ''
                    } ${c.className ?? ''}`}
                  >
                    {/* Footer over the FILTERED set (pre-paginate) — a total that
                        silently ignored the active filter would be the lying-fraction
                        defect the old reports table shipped. The row LABEL follows the
                        first VISIBLE footer-less column, so hiding or reordering
                        columns can't strand or drop it. */}
                    {c.footer
                      ? c.footer(sorted)
                      : i === footerLabelIndex
                        ? footerLabel?.(sorted)
                        : null}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <TableScrollRail scroller={scroller} controlsId={scrollerId} metrics={metrics} />
      <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
        <span aria-live="polite">
          {/* In server mode both numbers are real: `matched` is a COUNT over the whole
              table and the row count is this page. */}
          {server
            ? server.loading
              ? 'Loading…'
              : matched === 0
                ? 'No matches'
                : `${pageRows.length} of ${matched.toLocaleString()}${filtering ? ' matching' : ''}`
            : filtering
              ? `${sorted.length} of ${rows.length}`
              : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
        </span>
        {pageCount > 1 ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="rounded px-2 py-1 hover:bg-surface-2 disabled:opacity-40"
            >
              Prev
            </button>
            <span className="tabular-nums">
              {safePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
              disabled={safePage >= pageCount - 1}
              className="rounded px-2 py-1 hover:bg-surface-2 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
